import React, { useEffect, useRef, useState } from 'react';

import { renderTemplateForItem } from '../../utils/displayTemplate';
import { encode, isGs1, sanitizeGs1Value } from '../../utils/barcode/encode';

/**
 * One printed symbol, at true physical size.
 *
 * FAILURE IS PER BLOCK, NEVER PER SHEET. This is the standing rule a query
 * block already follows and the one that matters most here: an EAN-13
 * template that resolves to eleven digits on ONE item of a hundred-label run
 * must not take the other ninety-nine labels with it. So the encoder's
 * refusal is drawn as a bordered notice inside this block's own box, saying
 * what is wrong with the value, and every other block on the sheet prints.
 *
 * THE SYMBOL IS CENTRED IN ITS BOX AND NEVER STRETCHED — except vertically
 * for a LINEAR symbology, whose bar height carries no data and is the
 * author's to choose. A matrix symbol pulled out of square is unreadable, so
 * it keeps its own aspect even if the box is not square (the editor's
 * aspect lock keeps that from happening in the first place).
 *
 * WHY THE SVG IS INJECTED RATHER THAN PARSED. bwip-js emits a self-contained
 * `<svg>` of `<path>` elements it built itself — no user content reaches the
 * markup, because the payload is encoded into geometry, and human-readable
 * text is emitted as vector glyph PATHS rather than as `<text>` (which is
 * also why it needs no font at print time). dangerouslySetInnerHTML is the
 * cheap way to place it and there is nothing in it to sanitize.
 */

const asCss = (value, unit) => `${Number(value) || 0}${unit}`;

export default function BarcodeBlock({ block, item, elementDefs, resolveLookupValue, page }) {
	const [state, setState] = useState({ status: 'pending' });
	// A render that finished after the block moved on must not overwrite the
	// newer one: a print route re-renders a hundred records through the same
	// component and the encoder is async.
	const runRef = useRef(0);

	const payloadTemplate = block?.payload || '';
	const symbology = block?.symbology;
	const codeKey = JSON.stringify(block?.code || {});
	const itemId = item?._id || '';

	useEffect(() => {
		const run = ++runRef.current;
		let live = true;

		// The template is rendered against the item FIRST, so what reaches
		// the encoder is the literal string this label will carry. GS1
		// symbologies parse their payload as application-identifier syntax,
		// so every interpolated VALUE is reduced to characters an AI can
		// carry on the way through, while the author's own AI skeleton is
		// left alone.
		const payload = renderTemplateForItem({
			template: payloadTemplate,
			elements: item?.elements || {},
			elementDefinitions: elementDefs,
			resolveLookupValue,
			record: item,
			escapeValue: isGs1(symbology) ? sanitizeGs1Value : null,
		});

		encode({
			symbology,
			payload,
			module: block?.module,
			quiet: block?.quiet,
			rotate: block?.rotate || 'N',
			code: block?.code || {},
			unit: page?.unit || 'in',
			// The BLOCK, not just its height: a linear symbol's bars stretch
			// to it, and a symbol too big for it is an error rather than
			// something CSS quietly shrinks or clips.
			box: { w: block?.w, h: block?.h },
		}).then(result => {
			if (!live || run !== runRef.current) return;
			setState(result.error ? { status: 'error', error: result.error, payload } : { status: 'ready', ...result });
		});

		return () => { live = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [symbology, payloadTemplate, codeKey, block?.module, block?.quiet, block?.rotate, block?.w, block?.h, itemId, page?.unit, elementDefs, resolveLookupValue]);

	if (state.status === 'pending') {
		return <div className="page-block__barcode page-block__barcode--pending" aria-hidden="true" />;
	}

	if (state.status === 'error') {
		return (
			<div className="page-block__barcode-error" role="note" data-testid="barcode-error">
				<strong>Barcode</strong>
				<span>{state.error}</span>
			</div>
		);
	}

	const unit = page?.unit || 'in';
	// THE QUIET ZONE IS AN AXIS, NOT A BOX. For a matrix or stacked symbol
	// it is blank on all four sides. For a LINEAR one it is a left/right
	// requirement only — the bars have no top or bottom quiet zone — and
	// putting it on all four sides eats the bar height instead, which on a
	// short strip clamps the symbol to nothing and prints a blank white
	// rectangle with no error at all. A quarter turn takes the axis with it.
	// PHYSICAL sides, not logical ones: a page body's coordinates are already
	// physical (x is from the left edge of the sheet, whatever the reader's
	// writing direction), and a symbol is not text.
	const quietSize = asCss((state.quiet || 0) * state.module, unit);
	const quietStyle = state.family !== 'linear'
		? { padding: quietSize }
		: state.turned
			? { paddingTop: quietSize, paddingBottom: quietSize }
			: { paddingLeft: quietSize, paddingRight: quietSize };
	return (
		<div className="page-block__barcode" data-testid="barcode-symbol">
			<div
				className="page-block__barcode-symbol"
				style={{
					width: asCss(state.width, unit),
					height: asCss(state.height, unit),
					boxSizing: 'border-box',
					...quietStyle,
					// crispEdges stops a browser antialiasing a bar into its
					// neighbour's space, which is a scan failure on paper.
					shapeRendering: 'crispEdges',
				}}
				dangerouslySetInnerHTML={{ __html: state.svg }}
			/>
		</div>
	);
}
